import React from 'react';

/*
 * PROD-7828 — responsive data table (plan A4 shared/data_table.jsx).
 * Dual-render: <table> for wide containers, stacked cards under ~820px CONTAINER width
 * (container query, concierge_v3 precedent — not a viewport query, so it also handles
 * the drawer eating horizontal space). Props-only; reusable in teacher_web_app.
 *
 * columns: [{ key, label, render?(row), align?, hideInCard?, sort_key? }]
 * cardTitle(row) -> node shown as the card header on mobile.
 * rowClassName(row) -> optional extra class on the row/card (e.g. a highlight); both carry data-row-key.
 * sort {key, dir} + on_sort(sort_key): a column with sort_key gets a header button; the sorted one aria-sort.
 * Without on_sort the headers are plain text. Cards have no headers; the caller offers its own sort control.
 * onRowClick(row): a click anywhere on the row/card that did not land on a control inside it; a mouse
 * convenience only, so the caller must also render a button in the row for keyboard users.
 */
const INTERACTIVE = 'button, a, input, select, textarea, label';

const ARROWS = { ascending: '▲', descending: '▼', none: '' };

const aria_sort = (sort, key) => {
  if (!sort || sort.key !== key) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
};

// tp-col-align-<dir>: the module CSS keys tabular-nums and any other per-column styling off this class
// (column.align also sets the inline text-align, which the global th/td { text-align: center } cannot beat).
const col_class = (column) => (column.align ? `tp-col-align-${column.align}` : undefined);

const HeaderCell = ({ column, sort, on_sort }) => {
  const style = column.align ? { textAlign: column.align } : undefined;
  const className = col_class(column);
  if (!on_sort || !column.sort_key) {
    return (
      <th style={style} className={className}>
        {column.label}
      </th>
    );
  }
  const state = aria_sort(sort, column.sort_key);
  const arrow = ARROWS[state];
  return (
    <th style={style} className={className} aria-sort={state === 'none' ? undefined : state}>
      <button type="button" className="tp-th-sort" onClick={() => on_sort(column.sort_key)}>
        {column.label}
        <span className="tp-th-sort-arrow" aria-hidden="true">
          {arrow}
        </span>
      </button>
    </th>
  );
};

const DataTable = ({
  columns,
  rows,
  rowKey,
  cardTitle,
  emptyText,
  rowClassName,
  sort,
  on_sort,
  onRowClick
}) => {
  const extra = (row) =>
    [(rowClassName && rowClassName(row)) || '', onRowClick ? 'tp-row-click' : '']
      .filter(Boolean)
      .join(' ');
  const click = (row) =>
    onRowClick
      ? (e) => {
          if (e.target.closest && e.target.closest(INTERACTIVE)) return;
          onRowClick(row);
        }
      : undefined;
  if (!rows.length) {
    return <div className="tp-empty">{emptyText || 'No records for the selected filters.'}</div>;
  }
  return (
    <div className="tp-tablewrap">
      <table className="tp-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <HeaderCell key={c.key} column={c} sort={sort} on_sort={on_sort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            // Row click is a mouse convenience; the keyboard path is the caller's button in the row.
            <tr
              key={rowKey(row)}
              data-row-key={rowKey(row)}
              className={extra(row) || undefined}
              onClick={click(row)}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={col_class(c)}
                  style={c.align ? { textAlign: c.align } : undefined}
                >
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="tp-cards">
        {rows.map((row) => (
          // A mouse convenience only: the caller renders a button in the card for keyboard users.
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            key={rowKey(row)}
            data-row-key={rowKey(row)}
            className={`tp-card-item${extra(row) ? ` ${extra(row)}` : ''}`}
            onClick={click(row)}
          >
            <div className="tp-card-item-head">{cardTitle ? cardTitle(row) : null}</div>
            <div className="tp-card-item-grid">
              {columns
                .filter((c) => !c.hideInCard)
                .map((c) => (
                  <div key={c.key} className="tp-card-field">
                    <span className="tp-card-field-label">{c.label}</span>
                    <span>{c.render ? c.render(row) : row[c.key]}</span>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DataTable;
